import type { ApprovingInvoiceReportColumn } from '@/context/services/approving_invoice_reports';
import { saveListSnapshot } from '@/features/approving_invoice_reports/utils/listSnapshotStorage';
import { useKeepApprovalSearchConditionsFlag } from '@/hooks/feature_flag/useKeepApprovalSearchConditionsFlag';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  ActionMenuItems,
  IconButton,
  IconMoreVert,
  LinkButton,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './ActionCell.module.scss';

const cx = classnames.bind(styles);

export type ActionCellProps = {
  record: ApprovingInvoiceReportColumn;
  onOpenConfirmRejectModal: () => void;
  onConfirmApprove: (reportId: string) => void;
};

export const ActionCell: FC<ActionCellProps> = memo(
  ({ record, onOpenConfirmRejectModal, onConfirmApprove }) => {
    const { t } = useTranslation();
    const dropDownProps = useMemo(
      () => ({
        destroyPopupOnHide: true,
      }),
      []
    );

    const container = useGlobalContainerRef();
    const keepSearchConditionsEnabled = useKeepApprovalSearchConditionsFlag();

    // NA-5815 / NA-5831: remember the list URL and the request before leaving
    // for the detail page. The URL is read at click time.
    const saveSnapshot = () =>
      saveListSnapshot({ search: window.location.search, reportId: record.id });

    const detailLink = (
      <LinkButton
        isTransparent
        href={record.detail_path}
        containerClassName={cx('detailLink')}
      >
        {t('detail')}
      </LinkButton>
    );

    const actionMenuItems: ActionMenuItems = useMemo(() => {
      const items: ActionMenuItems = [];

      if (record.is_approvable) {
        items.push({
          key: 'approve',
          label: t('approve_invoice_report_confirm_button'),
          onClick: () => {
            onConfirmApprove(record.id);
          },
        });
      }

      if (record.is_report_approval) {
        items.push({
          key: 'reject',
          label: (
            <span className={cx('cancelLabel')}>
              {t('approving_invoice_report.drawer.reject_button')}
            </span>
          ),
          onClick: () => {
            onOpenConfirmRejectModal();
          },
        });
      }
      return items;
    }, [record, t, onOpenConfirmRejectModal, onConfirmApprove]);

    return (
      <div
        className={cx(styles['container'])}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Flag on: LinkButton has no onClick, so the click is observed on a
            wrapper. Flag off: the exact markup from before NA-5815. */}
        {keepSearchConditionsEnabled ? (
          <span onClick={saveSnapshot}>{detailLink}</span>
        ) : (
          detailLink
        )}
        {actionMenuItems.length > 0 && (
          <ActionMenu
            type='custom'
            items={actionMenuItems}
            dropDownProps={dropDownProps}
            getPopupContainer={container}
            className={cx('actionMenu')}
          >
            <IconButton size='xs' inVisible>
              <IconMoreVert size={20} />
            </IconButton>
          </ActionMenu>
        )}
      </div>
    );
  }
);

ActionCell.displayName = 'ActionCell';
